import React, { useState, useEffect } from "react";
import type { FormProps } from "antd";
import { Flex, Form, Upload, Select, message as antdMessage } from "antd";
import {
  StyledParagraph,
  StyledTitle,
  StyledSubmitButton,
  StyledInput,
  StyledTextArea,
  FormContainer,
  FormSection,
  LeftSection,
  RightSection,
} from "./styles";
import { PlusOutlined } from "@ant-design/icons";
import { Image } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { createNews, resetReportNews } from "../../Redux/Slice/ReportNewsSlice";
import { useKeycloak } from "@react-keycloak/web";
import NoPermission from "../../NoPermission";

type FieldType = {
  title?: string;
  content?: string;
  genres?: string;
  image?: any[];
  video?: any[];
};

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const ReportNews = () => {
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const [form] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [videoFileList, setVideoFileList] = useState<UploadFile[]>([]);

  const { loading, success, error, errorMessage } = useSelector(
    (state: any) => state.reportNews,
  );

  // Genre options (can be fetched from API if needed)
  const genreOptions = [
    { value: "General", label: "General" },
    { value: "Technology", label: "Technology" },
    { value: "Business", label: "Business" },
    { value: "Sports", label: "Sports" },
    { value: "Entertainment", label: "Entertainment" },
    { value: "Politics", label: "Politics" },
    { value: "Health", label: "Health" },
    { value: "Science", label: "Science" },
  ];

  useEffect(() => {
    if (success) {
      antdMessage.success("News created successfully!");
      form.resetFields();
      setFileList([]);
      setVideoFileList([]);
      dispatch(resetReportNews());
    }
    if (error) {
      antdMessage.error(errorMessage || "Failed to create news");
      dispatch(resetReportNews());
    }
  }, [success, error, errorMessage, dispatch, form]);

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    if (!keycloak.authenticated) {
      antdMessage.error("Please login to submit news");
      return;
    }

    console.log("Form values:", values);
    console.log("Image files:", values.image);
    console.log("Video files:", values.video);

    dispatch(
      createNews({
        title: values.title,
        content: values.content,
        genres: values.genres || "General",
        image: values.image,
        video: values.video,
        token: keycloak.token,
      }),
    );
  };

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = (
    errorInfo,
  ) => {
    console.log("Failed:", errorInfo);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldValue("image", newFileList);
  };

  const handleVideoChange: UploadProps["onChange"] = ({
    fileList: newFileList,
  }) => {
    setVideoFileList(newFileList);
    form.setFieldValue("video", newFileList);
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return keycloak.authenticated ? (
    <React.Fragment>
      <Flex vertical align="center">
        <StyledTitle>Report News</StyledTitle>
        <StyledParagraph>
          Report local news, incidents, and important updates from across
          Peshawar. Mera Peshawar empowers citizens to share authentic stories
          that matter to the community, helping keep the city informed and
          connected.
        </StyledParagraph>
      </Flex>
      <FormContainer>
        <Form
          form={form}
          name="basic"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <FormSection>
            <LeftSection>
              <Form.Item<FieldType>
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Title
                  </span>
                }
                name="title"
                rules={[
                  { required: true, message: "Please enter a proper title!" },
                ]}
              >
                <StyledInput style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item<FieldType>
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Content
                  </span>
                }
                name="content"
                rules={[{ required: true, message: "Please enter content!" }]}
              >
                <StyledTextArea
                  showCount
                  autoSize={{ minRows: 6, maxRows: 15 }}
                />
              </Form.Item>
              <Form.Item<FieldType>
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Genre
                  </span>
                }
                name="genres"
                initialValue="General"
              >
                <Select
                  placeholder="Select a genre"
                  options={genreOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </LeftSection>
            <RightSection>
              <Form.Item
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Image
                  </span>
                }
              >
                <Form.Item
                  name="image"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  noStyle
                >
                  <Upload
                    disabled
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    maxCount={1}
                    accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                    beforeUpload={() => false}
                    style={{ display: "flex", flexWrap: "wrap", width: "100%" }}
                  >
                    {fileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
                {previewImage && (
                  <Image
                    wrapperStyle={{ display: "none" }}
                    preview={{
                      visible: previewOpen,
                      onVisibleChange: (visible) => {
                        setPreviewOpen(visible);
                        if (!visible) {
                          setPreviewImage("");
                        }
                      },
                    }}
                    src={previewImage}
                  />
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Video
                  </span>
                }
              >
                <Form.Item
                  name="video"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  noStyle
                >
                  <Upload
                    disabled
                    listType="picture-card"
                    fileList={videoFileList}
                    onChange={handleVideoChange}
                    maxCount={1}
                    accept="video/mp4,video/avi,video/mov,video/wmv,video/flv,video/webm"
                    beforeUpload={() => false}
                    style={{ display: "flex", flexWrap: "wrap" }}
                  >
                    {videoFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Form.Item>
            </RightSection>
          </FormSection>
          <Form.Item>
            <StyledSubmitButton
              type="primary"
              htmlType="submit"
              loading={loading}
            >
              Submit Report
            </StyledSubmitButton>
          </Form.Item>
        </Form>
      </FormContainer>
    </React.Fragment>
  ) : (
    <NoPermission />
  );
};

export default ReportNews;
